import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, File as FileIcon, Folder as FolderIcon, Loader2, MessageSquareText } from 'lucide-react';
import { Link } from 'react-router-dom';
import heic2any from 'heic2any';
import { getContactInitialsClassName } from '../utils/colorUtils';
import { decodeFileName } from '../utils/fileNameUtils';
import {
    getHoverPreviewPreference,
    HOVER_PREVIEW_GROUPS,
    HOVER_PREVIEW_PREFERENCE_EVENT,
} from '../utils/previewPreferences';
import EmailPreviewContent from './EmailPreviewContent';
import '../styles/EntityHoverPreview.css';

const API_BASE = 'https://api.casedb.co';
const PREVIEW_CACHE_TTL = 60 * 1000;
const previewCache = new Map();

const parseJsonResponse = async (response) => {
    const payload = await response.json();
    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || payload?.error || `Request failed (${response.status})`);
    }
    return payload;
};

const fetchCasePreview = async (caseId, signal) => {
    const [casePayload, activityPayload] = await Promise.all([
        fetch(`${API_BASE}/cases.php?id=${encodeURIComponent(caseId)}`, {
            credentials: 'include',
            signal
        }).then(parseJsonResponse),
        fetch(`${API_BASE}/activity_feed.php?case_id=${encodeURIComponent(caseId)}&feed_type=All&page=1&per_page=3`, {
            credentials: 'include',
            signal
        }).then(parseJsonResponse).catch((error) => {
            if (error.name === 'AbortError') throw error;
            return { feed_updates: [] };
        })
    ]);

    return {
        entity: casePayload?.case || {},
        contact: casePayload?.contact || {},
        activities: Array.isArray(activityPayload?.feed_updates) ? activityPayload.feed_updates.slice(0, 3) : []
    };
};

const fetchJson = async (url, signal) => {
    const response = await fetch(url, { credentials: 'include', signal });
    const payload = await response.json();
    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || payload?.error || `Request failed (${response.status})`);
    }
    return payload;
};

const fetchDocumentPreview = async (documentId, signal, context = {}) => {
    const fileName = context.entityName || `Document ${documentId}`;
    const fileIdentifier = context.entitySource || fileName;
    const fileSize = Number(context.entitySize || 0);
    const fileType = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';

    if (fileSize > 500 * 1024 * 1024) {
        return { kind: 'large', fileType, fileSize };
    }

    const signedUrl = async (includePageCount = false) => fetchJson(
        `${API_BASE}/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true${includePageCount ? '&include_page_count=true' : ''}`,
        signal
    );

    if (fileType === 'pdf') {
        const response = await fetch(`${API_BASE}/pdf-proxy.php?fileKey=${encodeURIComponent(fileIdentifier)}&cases=true`, {
            credentials: 'include',
            signal
        });
        if (!response.ok) throw new Error(`Failed to load PDF (${response.status})`);
        const contentType = response.headers.get('Content-Type') || '';
        let pageCount = null;
        try {
            pageCount = (await signedUrl(true))?.page_count ?? null;
        } catch {
            pageCount = null;
        }
        if (contentType.includes('application/pdf')) {
            const blob = await response.blob();
            return { kind: 'pdf', sourceType: 'url', url: URL.createObjectURL(blob), pageCount };
        }
        const payload = await response.json();
        if (payload?.error) throw new Error(payload.error);
        if (payload?.type === 'base64') {
            return { kind: 'pdf', sourceType: 'base64', data: payload.data, pageCount };
        }
        throw new Error('PDF preview is unavailable');
    }

    if (['msg', 'eml'].includes(fileType)) {
        const payload = await fetchJson(
            `${API_BASE}/email-preview.php?fileKey=${encodeURIComponent(fileIdentifier)}&cases=true`,
            signal
        );
        if (!payload.preview) throw new Error('Failed to load email preview');
        return { kind: 'email', preview: payload.preview };
    }

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic'].includes(fileType)) {
        const payload = await signedUrl();
        if (fileType !== 'heic') return { kind: 'image', url: payload.url };
        try {
            const response = await fetch(payload.url, { signal });
            if (!response.ok) throw new Error('Failed to fetch HEIC file');
            const converted = await heic2any({
                blob: await response.blob(),
                toType: 'image/jpeg',
                quality: 0.8
            });
            return {
                kind: 'image',
                url: URL.createObjectURL(Array.isArray(converted) ? converted[0] : converted),
                heicConverted: true
            };
        } catch (error) {
            if (error.name === 'AbortError') throw error;
            return { kind: 'image', conversionError: error.message || 'Conversion error' };
        }
    }

    if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(fileType)) {
        return { kind: 'audio', url: (await signedUrl()).url };
    }

    if (['mp4', 'webm', 'mov'].includes(fileType)) {
        return { kind: 'video', url: (await signedUrl()).url };
    }

    if (fileType === 'ics' || ['txt', 'log', 'md', 'json', 'xml', 'csv', 'html', 'htm'].includes(fileType)) {
        const payload = await signedUrl(fileType !== 'ics');
        const response = await fetch(payload.url, { signal });
        if (!response.ok) throw new Error('Failed to load text preview');
        const text = await response.text();
        return {
            kind: fileType === 'ics' ? 'calendar' : 'text',
            text: text.length > 1000 ? `${text.substring(0, 1000)}...` : text,
            truncated: text.length > 1000
        };
    }

    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileType)) {
        const payload = await signedUrl(true);
        return {
            kind: 'office',
            url: payload.office_preview_url || payload.url,
            fileType
        };
    }

    return { kind: 'generic', fileType: fileType || 'file' };
};

const fetchFolderPreview = async (folderPath, signal, context = {}) => {
    const caseId = context.entitySource;
    if (!caseId) throw new Error('Folder preview unavailable');

    const params = new URLSearchParams({
        folder_fetch: 'true',
        case_id: String(caseId),
        current_folder: String(folderPath || ''),
        page: '1',
        page_size: '25',
    });
    const payload = await fetchJson(`${API_BASE}/documents.php?${params}`, signal);
    const items = Array.isArray(payload.items) ? payload.items : [];

    return {
        items,
        totalItems: Number(payload.pagination?.total_items ?? items.length),
        hasMore: Boolean(payload.pagination?.has_next),
    };
};

const entityLoaders = {
    case: fetchCasePreview,
    document: fetchDocumentPreview,
    folder: fetchFolderPreview,
};

const stripHtml = (value = '') => String(value)
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();

const formatPreviewDate = (value) => {
    if (!value) return '';
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
        ? `${value.replace(' ', 'T')}Z`
        : value;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    }).format(date);
};

const formatBytes = (value) => {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const getCaseImageUrl = (contact) => {
    const value = contact?.profile_picture || contact?.contact_display || '';
    if (!value || (!value.includes('contact-media.php') && !value.includes('uploads') && !value.startsWith('http'))) {
        return '';
    }
    return value.startsWith('http') ? value : `${API_BASE}/${value}`;
};

const CasePreviewContent = ({ data }) => {
    const caseData = data?.entity || {};
    const contact = data?.contact || {};
    const imageUrl = getCaseImageUrl(contact);
    const contactName = contact?.full_name || contact?.name || '';
    const avatarColorSeed = caseData?.case_id || caseData?.id || contact?.id;
    const avatarStyle = imageUrl
        ? undefined
        : {
            ...getContactInitialsClassName(avatarColorSeed, { color: true }),
            color: '#1d2125'
        };
    const initials = `${contact?.first_name?.trim()?.[0] || ''}${contact?.last_name?.trim()?.[0] || ''}`
        || contactName.trim()?.[0]
        || caseData?.case_name?.trim()?.[0]
        || 'C';

    return (
        <>
            <header className="entity-hover-preview-header">
                <div className="entity-hover-preview-identity">
                    <div className="entity-hover-preview-avatar" style={avatarStyle}>
                        {imageUrl ? <img src={imageUrl} alt="" /> : <span>{initials.toUpperCase()}</span>}
                    </div>
                    <div>
                        <span>Case {caseData?.case_id || caseData?.id}</span>
                        <strong title={caseData?.case_name}>{caseData?.case_name || 'Case preview'}</strong>
                        <small>{contactName || 'No primary contact listed'}</small>
                    </div>
                </div>
                <Link
                    to={`/case/${caseData?.case_id || caseData?.id}`}
                    aria-label={`Open ${caseData?.case_name || 'case'}`}
                >
                    <ExternalLink size={15} />
                </Link>
            </header>
            <div className="entity-hover-preview-body">
                <dl className="entity-hover-preview-vitals">
                    <div>
                        <dt>Phase</dt>
                        <dd>{caseData?.phase_name || 'Not set'}</dd>
                    </div>
                    <div>
                        <dt>Created</dt>
                        <dd>{formatPreviewDate(caseData?.create_date) || 'Not available'}</dd>
                    </div>
                    <div>
                        <dt>Updated</dt>
                        <dd>{formatPreviewDate(caseData?.updated_at) || 'Not available'}</dd>
                    </div>
                </dl>
                <section className="entity-hover-preview-activity">
                    <div className="entity-hover-preview-section-title">
                        <MessageSquareText size={14} />
                        <span>Recent activity</span>
                    </div>
                    {data.activities.length > 0 ? data.activities.map((activity) => {
                        const summary = stripHtml(activity?.subject || activity?.content || activity?.type || 'Activity');
                        return (
                            <div className="entity-hover-preview-activity-item" key={activity.id}>
                                <div>
                                    <strong>{activity.author || 'CaseDB'}</strong>
                                    <span>{formatPreviewDate(activity.creation_date)}</span>
                                </div>
                                <p>{summary || 'Activity update'}</p>
                            </div>
                        );
                    }) : (
                        <p className="entity-hover-preview-empty">No recent activity.</p>
                    )}
                </section>
            </div>
        </>
    );
};

const DocumentPreviewContent = ({ data, fileId, fileName }) => {
    const displayName = fileName || `Document ${fileId}`;

    const renderPreview = () => {
        switch (data?.kind) {
            case 'pdf':
                return data.sourceType === 'base64' ? (
                    <object
                        data={`data:application/pdf;base64,${data.data}`}
                        type="application/pdf"
                        aria-label={displayName}
                    />
                ) : (
                    <iframe
                        src={`${data.url}#page=1&view=FitH&toolbar=0&navpanes=0`}
                        title={displayName}
                    />
                );
            case 'email':
                return (
                    <EmailPreviewContent
                        preview={data.preview}
                        fallbackTitle={displayName}
                        containerStyle={{ maxHeight: '100%', overflowY: 'auto' }}
                    />
                );
            case 'image':
                return data.conversionError ? (
                    <div className="entity-hover-preview-native-state">
                        <FileIcon size={34} />
                        <span>{data.conversionError}</span>
                    </div>
                ) : <img src={data.url} alt={displayName} />;
            case 'audio':
                return <audio controls src={data.url} preload="metadata" />;
            case 'video':
                return <video controls src={data.url} preload="metadata" />;
            case 'calendar':
            case 'text':
                return (
                    <div className="entity-hover-preview-native-text">
                        <pre>{data.text}</pre>
                        {data.truncated && <small>Preview truncated.</small>}
                    </div>
                );
            case 'office':
                return (
                    <iframe
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(data.url)}&wdAspect=0&wdEmbedCode=0`}
                        title={displayName}
                    />
                );
            case 'large':
                return (
                    <div className="entity-hover-preview-native-state">
                        <FileIcon size={34} />
                        <span>This file is too large for an inline preview.</span>
                    </div>
                );
            default:
                return (
                    <div className="entity-hover-preview-native-state">
                        <FileIcon size={34} />
                        <span>{String(data?.fileType || 'File').toUpperCase()} preview is not available.</span>
                    </div>
                );
        }
    };

    return (
        <>
            <header className="entity-hover-preview-header">
                <div className="entity-hover-preview-document-heading">
                    <span>Document preview</span>
                    <strong title={displayName}>{displayName}</strong>
                    <small>{data?.kind === 'pdf' && data?.pageCount ? `${data.pageCount} pages` : String(data?.kind || 'file')}</small>
                </div>
                <Link to={`/file/${fileId}`} aria-label={`Open ${displayName}`}>
                    <ExternalLink size={15} />
                </Link>
            </header>
            <div className="entity-hover-preview-document-content">
                {renderPreview()}
            </div>
        </>
    );
};

const FolderPreviewContent = ({ data, folderName }) => (
    <>
        <header className="entity-hover-preview-header">
            <div className="entity-hover-preview-identity">
                <div className="entity-hover-preview-avatar folder">
                    <FolderIcon size={21} />
                </div>
                <div>
                    <span>Folder contents</span>
                    <strong title={folderName}>{folderName || 'Folder'}</strong>
                    <small>{data.totalItems} {data.totalItems === 1 ? 'item' : 'items'}</small>
                </div>
            </div>
        </header>
        <div className="entity-hover-preview-folder-content">
            {data.items.length === 0 ? (
                <div className="entity-hover-preview-folder-empty">This folder is empty.</div>
            ) : (
                <div className="entity-hover-preview-folder-list">
                    {data.items.map((item, index) => {
                        const isFolder = item.type === 'folder';
                        const name = decodeFileName(item.name || item.key?.split('/').filter(Boolean).pop() || 'Untitled');
                        return (
                            <div className="entity-hover-preview-folder-item" key={item.key || `${name}-${index}`}>
                                {isFolder ? <FolderIcon size={16} /> : <FileIcon size={16} />}
                                <span title={name}>{name}</span>
                                <small>{isFolder ? `${item.size || 0} items` : formatBytes(item.size)}</small>
                            </div>
                        );
                    })}
                    {data.hasMore && (
                        <div className="entity-hover-preview-folder-more">
                            More items are available inside this folder.
                        </div>
                    )}
                </div>
            )}
        </div>
    </>
);

export const EntityHoverPreview = ({
    entityType,
    entityId,
    entityName = '',
    entitySource = '',
    entitySize = 0,
    children,
    as: Trigger = 'span',
    className = '',
    openDelay = 450,
    excludeSelector = '',
    ...triggerProps
}) => {
    const triggerRef = useRef(null);
    const previewRef = useRef(null);
    const openTimerRef = useRef(null);
    const closeTimerRef = useRef(null);
    const requestRef = useRef(null);
    const pointerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [state, setState] = useState({ loading: false, data: null, error: '' });
    const [position, setPosition] = useState({
        top: -10000,
        left: -10000,
        width: 420,
        maxHeight: 430,
        placement: 'below'
    });
    const normalizedType = String(entityType || '').toLowerCase();
    const previewGroup = normalizedType === 'document' || normalizedType === 'folder'
        ? HOVER_PREVIEW_GROUPS.DOCUMENTS
        : HOVER_PREVIEW_GROUPS.RECORDS;
    const [previewEnabled, setPreviewEnabled] = useState(() => getHoverPreviewPreference(previewGroup));
    const cacheKey = `${normalizedType}:${entitySource}:${entityId}`;
    const canPreview = Boolean(previewEnabled && entityId && entityLoaders[normalizedType]);
    const isExcludedTarget = useCallback((target, clientX, clientY) => {
        if (!excludeSelector) return false;
        const pointerTarget = Number.isFinite(clientX) && Number.isFinite(clientY)
            ? document.elementFromPoint(clientX, clientY)
            : null;
        return Boolean(
            pointerTarget?.closest?.(excludeSelector)
            || target?.closest?.(excludeSelector)
        );
    }, [excludeSelector]);

    const clearTimers = useCallback(() => {
        if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
        openTimerRef.current = null;
        closeTimerRef.current = null;
    }, []);

    useEffect(() => {
        setPreviewEnabled(getHoverPreviewPreference(previewGroup));

        const handlePreferenceChange = (event) => {
            if (event.detail?.group !== previewGroup) return;
            setPreviewEnabled(event.detail.enabled !== false);
            if (event.detail.enabled === false) {
                clearTimers();
                requestRef.current?.abort();
                setOpen(false);
            }
        };

        window.addEventListener(HOVER_PREVIEW_PREFERENCE_EVENT, handlePreferenceChange);
        return () => window.removeEventListener(HOVER_PREVIEW_PREFERENCE_EVENT, handlePreferenceChange);
    }, [clearTimers, previewGroup]);

    const showPreview = useCallback((immediate = false) => {
        if (!canPreview) return;
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
        if (open || openTimerRef.current) return;
        openTimerRef.current = window.setTimeout(() => {
            openTimerRef.current = null;
            setOpen(true);
        }, immediate ? 0 : openDelay);
    }, [canPreview, open, openDelay]);

    const hidePreview = useCallback(() => {
        if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = window.setTimeout(() => {
            closeTimerRef.current = null;
            setOpen(false);
        }, 220);
    }, []);

    const updatePosition = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const viewportPadding = 12;
        const gap = 10;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const navCompactTop = Array.from(document.querySelectorAll('.nav-compact, .nav-compact-mini'))
            .map((element) => {
                const styles = window.getComputedStyle(element);
                const elementRect = element.getBoundingClientRect();
                if (
                    styles.display === 'none'
                    || styles.visibility === 'hidden'
                    || Number(styles.opacity) === 0
                    || elementRect.width <= 0
                    || elementRect.height <= 0
                    || elementRect.bottom <= 0
                    || elementRect.top >= viewportHeight
                ) {
                    return null;
                }
                return elementRect.top;
            })
            .filter(Number.isFinite)
            .reduce((top, candidateTop) => Math.min(top, candidateTop), viewportHeight);
        const usableViewportBottom = Math.min(
            viewportHeight - viewportPadding,
            navCompactTop < viewportHeight ? navCompactTop - gap : viewportHeight - viewportPadding
        );
        const width = Math.min(440, viewportWidth - (viewportPadding * 2));
        const preferredHeight = Math.min(
            normalizedType === 'document' ? 520 : 430,
            Math.max(0, usableViewportBottom - viewportPadding)
        );
        const measuredHeight = Math.min(
            previewRef.current?.offsetHeight || preferredHeight,
            preferredHeight
        );
        const clampLeft = (value) => Math.min(
            viewportWidth - width - viewportPadding,
            Math.max(viewportPadding, value)
        );
        const applyPosition = (nextPosition) => {
            setPosition((currentPosition) => (
                currentPosition.top === nextPosition.top
                && currentPosition.left === nextPosition.left
                && currentPosition.width === nextPosition.width
                && currentPosition.maxHeight === nextPosition.maxHeight
                && currentPosition.placement === nextPosition.placement
                    ? currentPosition
                    : nextPosition
            ));
        };
        const belowRoom = Math.max(0, usableViewportBottom - rect.bottom - gap);
        const aboveRoom = Math.max(0, rect.top - viewportPadding - gap);
        const placement = belowRoom >= preferredHeight
            ? 'below'
            : aboveRoom >= preferredHeight
                ? 'above'
                : belowRoom >= aboveRoom ? 'below' : 'above';
        const availableHeight = placement === 'below' ? belowRoom : aboveRoom;
        const constrainedHeight = Math.min(preferredHeight, availableHeight);
        const renderedHeight = Math.min(measuredHeight, constrainedHeight);
        const isRowTrigger = triggerRef.current?.tagName === 'TR';
        const pointerX = pointerRef.current?.x;
        const horizontalCenter = (isRowTrigger || rect.width > width) && Number.isFinite(pointerX)
            ? pointerX
            : rect.left + (rect.width / 2);

        applyPosition({
            top: placement === 'below'
                ? rect.bottom + gap
                : Math.max(viewportPadding, rect.top - gap - renderedHeight),
            left: clampLeft(horizontalCenter - (width / 2)),
            width,
            maxHeight: constrainedHeight,
            placement
        });
    }, [normalizedType]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updatePosition)
            : null;
        if (previewRef.current) resizeObserver?.observe(previewRef.current);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            resizeObserver?.disconnect();
        };
    }, [open, state.loading, state.data, state.error, updatePosition]);

    useEffect(() => {
        if (!open || !canPreview) return undefined;
        const cached = previewCache.get(cacheKey);
        if (cached && Date.now() - cached.loadedAt < PREVIEW_CACHE_TTL) {
            setState({ loading: false, data: cached.data, error: '' });
            return undefined;
        }

        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        setState({ loading: true, data: null, error: '' });
        entityLoaders[normalizedType](entityId, controller.signal, {
            entityName,
            entitySource,
            entitySize
        })
            .then((data) => {
                previewCache.set(cacheKey, { data, loadedAt: Date.now() });
                setState({ loading: false, data, error: '' });
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    setState({ loading: false, data: null, error: error.message || 'Preview unavailable' });
                }
            });
        return () => controller.abort();
    }, [cacheKey, canPreview, entityId, entityName, entitySize, entitySource, normalizedType, open]);

    useEffect(() => () => {
        clearTimers();
        requestRef.current?.abort();
    }, [clearTimers]);

    return (
        <>
            <Trigger
                {...triggerProps}
                ref={triggerRef}
                className={`entity-hover-preview-trigger ${className}`.trim()}
                onMouseEnter={(event) => {
                    triggerProps.onMouseEnter?.(event);
                    if (isExcludedTarget(event.target, event.clientX, event.clientY)) return;
                    pointerRef.current = { x: event.clientX, y: event.clientY };
                    showPreview(false);
                }}
                onMouseMove={(event) => {
                    triggerProps.onMouseMove?.(event);
                    if (isExcludedTarget(event.target, event.clientX, event.clientY)) {
                        hidePreview();
                        return;
                    }
                    if (!open) {
                        pointerRef.current = { x: event.clientX, y: event.clientY };
                        showPreview(false);
                    }
                }}
                onMouseLeave={(event) => {
                    triggerProps.onMouseLeave?.(event);
                    hidePreview();
                }}
                onFocusCapture={(event) => {
                    triggerProps.onFocusCapture?.(event);
                    if (isExcludedTarget(event.target)) return;
                    showPreview(true);
                }}
                onBlurCapture={(event) => {
                    triggerProps.onBlurCapture?.(event);
                    if (!previewRef.current?.contains(event.relatedTarget)) hidePreview();
                }}
            >
                {children}
            </Trigger>
            {open && createPortal(
                <aside
                    ref={previewRef}
                    className={`entity-hover-preview entity-type-${normalizedType} placement-${position.placement}`}
                    style={{
                        top: position.top,
                        left: position.left,
                        width: position.width,
                        maxHeight: position.maxHeight
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onMouseEnter={() => {
                        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
                        closeTimerRef.current = null;
                    }}
                    onMouseLeave={hidePreview}
                    aria-label={`${normalizedType} preview`}
                >
                    {state.loading && (
                        <div className="entity-hover-preview-state">
                            <Loader2 size={18} className="spinner" />
                            <span>Loading preview…</span>
                        </div>
                    )}
                    {!state.loading && state.error && (
                        <div className="entity-hover-preview-state error">{state.error}</div>
                    )}
                    {!state.loading && state.data && normalizedType === 'case' && (
                        <CasePreviewContent data={state.data} />
                    )}
                    {!state.loading && state.data && normalizedType === 'document' && (
                        <DocumentPreviewContent data={state.data} fileId={entityId} fileName={entityName} />
                    )}
                    {!state.loading && state.data && normalizedType === 'folder' && (
                        <FolderPreviewContent data={state.data} folderName={entityName} />
                    )}
                </aside>,
                document.body
            )}
        </>
    );
};
